import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import ServerSideDataTable from './ServerSideDataTable';
import Options from './Options';

/**
 * A turn's rows, beside the answer instead of inside it.
 *
 * The transcript keeps the prose at a readable width; the rows get a surface
 * that stays put while the transcript scrolls, so an answer and the data behind
 * it can be read at the same time. Pinned to one turn by a press, and never
 * re-pointed by a later answer arriving — the rail's owner holds the selection,
 * so this component renders what it is handed and asks for changes.
 *
 * One component, two positions. Beside the column it is a complementary region:
 * nothing is blocked, so nothing is trapped. Narrower than that it covers the
 * transcript, which makes it a modal dialog — scrim, focus trap, and focus
 * handed back to whatever opened it. `isOverlay` is decided by its owner, which
 * already watches the width, rather than by a second media query in here.
 */
export default function DataRail({ view, sessionId, onSelectOutput, onClose, isOverlay = false, width, onResize }) {
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);

  // Captured on mount and restored on unmount: closing a modal and leaving
  // focus at the top of the document strands anyone not using a mouse.
  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    return () => {
      const target = returnFocusRef.current;
      if (target && typeof target.focus === 'function' && document.contains(target)) target.focus();
    };
  }, []);

  useEffect(() => {
    if (isOverlay) panelRef.current?.focus();
  }, [isOverlay]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      // Tab wrapping only while this covers the page. Beside the column the
      // transcript is still there to be tabbed into, and trapping focus in a
      // panel nobody is blocked by is a cage.
      if (!isOverlay || event.key !== 'Tab') return;
      const focusable = panelRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOverlay, onClose]);

  const startResize = (event) => {
    if (isOverlay || !onResize) return;
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width || 420;
    // The rail is anchored right, so dragging the handle LEFT makes it wider.
    const onMove = (move) => onResize(startWidth + (startX - move.clientX));
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.style.removeProperty('user-select');
    };
    // Without this the drag selects the table text it passes over.
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const nudge = (event) => {
    if (!onResize) return;
    // Keyboard parity for a drag: a handle only a mouse can move is a control
    // some readers do not have.
    if (event.key === 'ArrowLeft') onResize((width || 420) + 32);
    else if (event.key === 'ArrowRight') onResize((width || 420) - 32);
    else return;
    event.preventDefault();
  };

  if (!view) return null;

  const { message, question, resultSet, output } = view;
  const label = question ? `Rows for “${question}”` : 'Rows for this answer';
  const rowCount = typeof output.totalRows === 'number' ? output.totalRows : output.rows.length;

  const panel = (
    <div
      ref={panelRef}
      tabIndex={-1}
      role={isOverlay ? 'dialog' : 'complementary'}
      aria-modal={isOverlay ? 'true' : undefined}
      aria-label={label}
      className={
        isOverlay
          ? 'animate-rise fixed inset-y-0 right-0 z-[2000] flex w-[92vw] max-w-[520px] flex-col border-l border-line bg-surface'
          : 'relative flex h-full min-h-0 flex-col border-l border-line bg-surface'
      }
      style={isOverlay ? { boxShadow: 'var(--shadow-float)' } : undefined}
    >
      {!isOverlay && onResize && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize data panel"
          aria-valuenow={width}
          tabIndex={0}
          onPointerDown={startResize}
          onKeyDown={nudge}
          className="absolute inset-y-0 left-0 z-10 w-1.5 -translate-x-1/2 cursor-col-resize bg-transparent transition-colors hover:bg-brand focus-visible:bg-brand focus-visible:outline-none"
        />
      )}

      <div className="flex items-start gap-2 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Data</p>
          {/* Which turn these rows came from. The rail outlives the answer's
              position on screen, so without this the rows are unattributed. */}
          <p className="truncate text-sm font-medium text-fg" title={question || undefined}>
            {question || 'This answer'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close data panel"
          className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-fg-muted transition-colors hover:bg-surface-raised hover:text-fg"
        >
          <X aria-hidden="true" size={16} />
        </button>
      </div>

      {resultSet.isMultiSource && (
        <div
          role="tablist"
          aria-label="Outputs"
          className="flex gap-1 overflow-x-auto border-b border-line px-3 py-2"
          // Roving tabindex: one Tab stop for the whole strip, arrows to move
          // within it. The two halves are inseparable — parking unselected tabs
          // at tabIndex -1 without arrows would make them unreachable by
          // keyboard entirely, which is worse than the extra stops it fixes.
          onKeyDown={(event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            const index = resultSet.outputs.findIndex((candidate) => candidate.key === output.key);
            const step = event.key === 'ArrowRight' ? 1 : -1;
            const next = resultSet.outputs[(index + step + resultSet.outputs.length) % resultSet.outputs.length];
            onSelectOutput(next.key);
          }}
        >
          {resultSet.outputs.map((candidate) => {
            const selected = candidate.key === output.key;
            return (
              <button
                key={candidate.key ?? candidate.label}
                type="button"
                role="tab"
                aria-selected={selected}
                tabIndex={selected ? 0 : -1}
                onClick={() => onSelectOutput(candidate.key)}
                className={`shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  selected ? 'bg-surface-raised text-fg' : 'text-fg-muted hover:bg-surface-raised hover:text-fg'
                }`}
              >
                {candidate.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto px-3 py-3">
        <ServerSideDataTable
          key={`${message.id}:${output.key ?? 'result'}`}
          initialData={output.rows}
          totalCount={rowCount}
          messageId={message.id}
          sessionId={sessionId}
          sourceKey={output.key ?? undefined}
          fetchOnMount={!!resultSet.needsFetch}
        />
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5">
        <span className="text-xs text-fg-muted">
          {/* The count arrives with the rows when they had to be fetched, so
              claiming "0 rows" while they load would be wrong twice over. */}
          {resultSet.needsFetch ? 'Loading rows…' : `${rowCount.toLocaleString()} ${rowCount === 1 ? 'row' : 'rows'}`}
        </span>
        {/* Row exports live with the rows. `answer` is deliberately absent, so
            this offers CSV and Excel and not the answer's own exports — those
            stay under the message they describe. */}
        <Options
          id={message.id}
          sessionId={sessionId}
          sourceKey={output.key ?? undefined}
          isMultiSource={resultSet.isMultiSource}
          canDownloadData
          tooltipText="Download rows"
        />
      </div>
    </div>
  );

  if (!isOverlay) return panel;

  return (
    <>
      <div
        data-rail-scrim
        onMouseDown={onClose}
        className="fixed inset-0 z-[1999] bg-black/40 backdrop-blur-sm"
      />
      {panel}
    </>
  );
}

DataRail.propTypes = {
  // The resolved view from `railFor` — the turn, its result set, and which
  // output is on screen. Resolved by the owner so this component holds no
  // state that could disagree with the transcript.
  view: PropTypes.shape({
    message: PropTypes.object.isRequired,
    question: PropTypes.string,
    resultSet: PropTypes.object.isRequired,
    output: PropTypes.object.isRequired,
  }),
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectOutput: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOverlay: PropTypes.bool,
  // Present only beside the column: an overlay is sized by the viewport.
  width: PropTypes.number,
  onResize: PropTypes.func,
};

